// store/slices/adminSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import adminService, { UserFilters } from "../../services/adminService";

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface KycDocument {
  id: number;
  user_id: number;
  document_type: string;
  document_number?: string;
  front_image: string;
  back_image?: string;
  selfie_image?: string;
  status: "pending" | "approved" | "rejected";
  rejection_reason?: string;
  reviewed_by?: number;
  reviewed_at?: string;
  created_at: string;
  updated_at: string;
  user?: {
    id: number;
    username: string;
    email: string;
    first_name?: string;
    last_name?: string;
    kyc_status: string;
  };
}

interface Transaction {
  id: number;
  user_id: number;
  type:
    | "deposit"
    | "withdrawal"
    | "trade_earning"
    | "airdrop"
    | "commission"
    | "bot_earning"
    | "referral_earning";
  amount: number;
  status: "pending" | "completed" | "rejected";
  tx_id?: string;
  tx_hash?: string;
  destination_address?: string;
  memo?: string;
  metadata?: any;
  created_at: string;
  updated_at: string;
  user?: {
    id: number;
    username: string;
    email: string;
  };
}

interface XrpAddress {
  id: number;
  address: string;
  qr_code?: string;
  qr_code_url?: string;
  destination_tag?: string;
  memo_type?: string;
  label?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface Airdrop {
  id: number;
  name: string;
  description?: string;
  amount: number;
  distribution_date: string;
  criteria?: any;
  status: "pending" | "active" | "completed" | "cancelled";
  created_at: string;
  updated_at: string;
}

interface TradingSettings {
  daily_rate: number;
  is_active: boolean;
  min_balance_for_earnings: number;
  total_distributed: number;
  total_users_earning: number;
  last_distribution: string | null;
  xrp_price: number;
}

interface DashboardStats {
  users: {
    total: number;
    active: number;
    pending_kyc: number;
  };
  transactions: {
    today_deposits: number;
    today_withdrawals: number;
    pending_deposits: number;
    pending_withdrawals: number;
  };
  total_balance: number;
}

// ─── UPDATED User interface ───────────────────────────────────────────────────
// Added: referred_by, referral_code, total_referral_earnings, referrer
// `referrer` is eager-loaded by the backend in getUsers() and contains the
// full referrer object (or null for organic signups) — no secondary API call.

interface User {
  id: number;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  xrp_balance: number;
  kyc_status: string;
  role: string;
  is_active: boolean;
  email_verified_at?: string;
  created_at: string;
  kyc_document?: KycDocument;

  // Referral fields
  referred_by?: number | null;
  referral_code?: string | null;
  total_referral_earnings?: number | null;

  // Eager-loaded by getUsers() — the referrer's public profile or null
  referrer?: {
    id: number;
    username: string;
    email: string;
    referral_code?: string;
  } | null;
}

interface TradingStatistics {
  settings: {
    daily_rate: number;
    is_active: boolean;
    min_balance_for_earnings: number;
  };
  statistics: {
    total_users: number;
    eligible_users: number;
    total_balance: number;
    estimated_daily_earnings: number;
    total_distributed: number;
    total_users_earning: number;
    last_distribution: string | null;
    xrp_price: number;
  };
}

interface PaginatedResponse<T> {
  data: T[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number;
  to: number;
}

// ─── State ────────────────────────────────────────────────────────────────────

interface AdminState {
  // Users
  users: User[];
  pendingKyc: KycDocument[];
  allKycDocuments: KycDocument[];

  // Deposits & Withdrawals
  pendingDeposits: Transaction[];
  pendingWithdrawals: Transaction[];
  allDeposits: Transaction[];
  allWithdrawals: Transaction[];

  // XRP Addresses
  xrpAddresses: XrpAddress[];

  // Airdrops
  airdrops: Airdrop[];

  // Trading
  tradingSettings: TradingSettings | null;
  tradingStatistics: TradingStatistics | null;

  // Dashboard
  dashboardStats: DashboardStats | null;

  // Pagination
  totalPages: number;
  currentPage: number;
  totalItems: number;

  // UI States
  isLoading: boolean;
  actionLoading: boolean;
  error: string | null;
  success: string | null;
}

const initialState: AdminState = {
  users: [],
  pendingKyc: [],
  allKycDocuments: [],
  pendingDeposits: [],
  pendingWithdrawals: [],
  allDeposits: [],
  allWithdrawals: [],
  xrpAddresses: [],
  airdrops: [],
  tradingSettings: null,
  tradingStatistics: null,
  dashboardStats: null,
  totalPages: 1,
  currentPage: 1,
  totalItems: 0,
  isLoading: false,
  actionLoading: false,
  error: null,
  success: null,
};

// ─── Async Thunks ─────────────────────────────────────────────────────────────

// ── Dashboard ─────────────────────────────────────────────────────────────────

export const getDashboardStats = createAsyncThunk(
  "admin/getDashboardStats",
  async (_, { rejectWithValue }) => {
    try {
      return await adminService.getDashboardStats();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch dashboard stats",
      );
    }
  },
);

// ── User Management ───────────────────────────────────────────────────────────

export const getUsers = createAsyncThunk<
  PaginatedResponse<User>,
  UserFilters, // Use the UserFilters type directly
  { rejectValue: string }
>("admin/getUsers", async (filters, { rejectWithValue }) => {
  try {
    return await adminService.getUsers(filters);
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch users",
    );
  }
});

export const toggleUserStatus = createAsyncThunk(
  "admin/toggleUserStatus",
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await adminService.toggleUserStatus(userId);
      return { userId, ...response };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to toggle user status",
      );
    }
  },
);

export const updateUserRole = createAsyncThunk(
  "admin/updateUserRole",
  async (
    { userId, role }: { userId: number; role: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await adminService.updateUserRole(userId, role);
      return { userId, ...response };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update user role",
      );
    }
  },
);

// ── KYC Management ────────────────────────────────────────────────────────────

export const getPendingKyc = createAsyncThunk(
  "admin/getPendingKyc",
  async (_, { rejectWithValue }) => {
    try {
      return await adminService.getPendingKyc();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch pending KYC",
      );
    }
  },
);

export const getAllKycDocuments = createAsyncThunk<
  PaginatedResponse<KycDocument>,
  any,
  { rejectValue: string }
>("admin/getAllKycDocuments", async (filters, { rejectWithValue }) => {
  try {
    return await adminService.getAllKycDocuments(filters);
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch KYC documents",
    );
  }
});

export const approveKyc = createAsyncThunk(
  "admin/approveKyc",
  async (
    { kycId, notes }: { kycId: number; notes?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await adminService.approveKyc(kycId, notes);
      return { kycId, ...response };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to approve KYC",
      );
    }
  },
);

export const rejectKyc = createAsyncThunk(
  "admin/rejectKyc",
  async (
    { kycId, rejectionReason }: { kycId: number; rejectionReason: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await adminService.rejectKyc(kycId, rejectionReason);
      return { kycId, ...response };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reject KYC",
      );
    }
  },
);

// ── Deposit Management ────────────────────────────────────────────────────────

export const getPendingDeposits = createAsyncThunk(
  "admin/getPendingDeposits",
  async (_, { rejectWithValue }) => {
    try {
      return await adminService.getPendingDeposits();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch pending deposits",
      );
    }
  },
);

export const getAllDeposits = createAsyncThunk<
  PaginatedResponse<Transaction>,
  any,
  { rejectValue: string }
>("admin/getAllDeposits", async (filters, { rejectWithValue }) => {
  try {
    return await adminService.getAllDeposits(filters);
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch deposits",
    );
  }
});

export const confirmDeposit = createAsyncThunk(
  "admin/confirmDeposit",
  async (
    {
      transactionId,
      txHash,
      adminNotes,
      isAdminCredit, // ← NEW
    }: {
      transactionId: number;
      txHash: string;
      adminNotes?: string;
      isAdminCredit?: boolean; // ← NEW
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await adminService.confirmDeposit(
        transactionId,
        txHash,
        adminNotes,
        isAdminCredit, // ← NEW
      );
      return { transactionId, ...response };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to confirm deposit",
      );
    }
  },
);

export const rejectDeposit = createAsyncThunk(
  "admin/rejectDeposit",
  async (
    {
      transactionId,
      rejectionReason,
    }: { transactionId: number; rejectionReason: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await adminService.rejectDeposit(
        transactionId,
        rejectionReason,
      );
      return { transactionId, ...response };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reject deposit",
      );
    }
  },
);

// ── Withdrawal Management ─────────────────────────────────────────────────────

export const getPendingWithdrawals = createAsyncThunk(
  "admin/getPendingWithdrawals",
  async (_, { rejectWithValue }) => {
    try {
      return await adminService.getPendingWithdrawals();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch pending withdrawals",
      );
    }
  },
);

export const getAllWithdrawals = createAsyncThunk<
  PaginatedResponse<Transaction>,
  any,
  { rejectValue: string }
>("admin/getAllWithdrawals", async (filters, { rejectWithValue }) => {
  try {
    return await adminService.getAllWithdrawals(filters);
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch withdrawals",
    );
  }
});

export const processWithdrawal = createAsyncThunk(
  "admin/processWithdrawal",
  async (
    {
      transactionId,
      txHash,
      adminNotes,
    }: { transactionId: number; txHash: string; adminNotes?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await adminService.processWithdrawal(
        transactionId,
        txHash,
        adminNotes,
      );
      return { transactionId, ...response };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to process withdrawal",
      );
    }
  },
);

export const rejectWithdrawal = createAsyncThunk(
  "admin/rejectWithdrawal",
  async (
    {
      transactionId,
      rejectionReason,
    }: { transactionId: number; rejectionReason: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await adminService.rejectWithdrawal(
        transactionId,
        rejectionReason,
      );
      return { transactionId, ...response };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reject withdrawal",
      );
    }
  },
);

// ── XRP Address Management ────────────────────────────────────────────────────

export const getXrpAddresses = createAsyncThunk(
  "admin/getXrpAddresses",
  async (_, { rejectWithValue }) => {
    try {
      return await adminService.getXrpAddresses();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch XRP addresses",
      );
    }
  },
);

export const createXrpAddress = createAsyncThunk(
  "admin/createXrpAddress",
  async (addressData: any, { rejectWithValue }) => {
    try {
      return await adminService.createXrpAddress(addressData);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create XRP address",
      );
    }
  },
);

export const updateXrpAddress = createAsyncThunk(
  "admin/updateXrpAddress",
  async (
    { id, addressData }: { id: number; addressData: any },
    { rejectWithValue },
  ) => {
    try {
      return await adminService.updateXrpAddress(id, addressData);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update XRP address",
      );
    }
  },
);

export const deleteXrpAddress = createAsyncThunk(
  "admin/deleteXrpAddress",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await adminService.deleteXrpAddress(id);
      return { id, ...response };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete XRP address",
      );
    }
  },
);

export const uploadQrCode = createAsyncThunk(
  "admin/uploadQrCode",
  async ({ id, qrCode }: { id: number; qrCode: File }, { rejectWithValue }) => {
    try {
      const response = await adminService.uploadQrCode(id, qrCode);
      return { id, ...response };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to upload QR code",
      );
    }
  },
);

// ── Airdrop Management ────────────────────────────────────────────────────────

export const getAirdrops = createAsyncThunk(
  "admin/getAirdrops",
  async (_, { rejectWithValue }) => {
    try {
      return await adminService.getAirdrops();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch airdrops",
      );
    }
  },
);

export const createAirdrop = createAsyncThunk(
  "admin/createAirdrop",
  async (airdropData: any, { rejectWithValue }) => {
    try {
      return await adminService.createAirdrop(airdropData);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create airdrop",
      );
    }
  },
);

export const toggleAirdropStatus = createAsyncThunk(
  "admin/toggleAirdropStatus",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await adminService.toggleAirdropStatus(id);
      return { id, ...response };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to toggle airdrop status",
      );
    }
  },
);

export const distributeAirdrop = createAsyncThunk(
  "admin/distributeAirdrop",
  async (id: number, { rejectWithValue }) => {
    try {
      return await adminService.distributeAirdrop(id);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to distribute airdrop",
      );
    }
  },
);

// ── Trading Management ────────────────────────────────────────────────────────

export const getTradingSettings = createAsyncThunk(
  "admin/getTradingSettings",
  async (_, { rejectWithValue }) => {
    try {
      return await adminService.getTradingSettings();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch trading settings",
      );
    }
  },
);

export const updateTradingSettings = createAsyncThunk(
  "admin/updateTradingSettings",
  async (settingsData: any, { rejectWithValue }) => {
    try {
      return await adminService.updateTradingSettings(settingsData);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update trading settings",
      );
    }
  },
);

export const toggleTradingStatus = createAsyncThunk(
  "admin/toggleTradingStatus",
  async (_, { rejectWithValue }) => {
    try {
      return await adminService.toggleTradingStatus();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to toggle trading status",
      );
    }
  },
);

export const manualDistribution = createAsyncThunk(
  "admin/manualDistribution",
  async (_, { rejectWithValue }) => {
    try {
      return await adminService.manualDistribution();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to run manual distribution",
      );
    }
  },
);

export const getTradingStatistics = createAsyncThunk(
  "admin/getTradingStatistics",
  async (_, { rejectWithValue }) => {
    try {
      return await adminService.getTradingStatistics();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch trading statistics",
      );
    }
  },
);

// ── System Actions ────────────────────────────────────────────────────────────

export const recalculateEarnings = createAsyncThunk(
  "admin/recalculateEarnings",
  async (_, { rejectWithValue }) => {
    try {
      return await adminService.recalculateEarnings();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to recalculate earnings",
      );
    }
  },
);

export const checkSystemHealth = createAsyncThunk(
  "admin/checkSystemHealth",
  async (_, { rejectWithValue }) => {
    try {
      return await adminService.checkSystemHealth();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to check system health",
      );
    }
  },
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    resetAdminState: () => initialState,
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder

      // ── Dashboard ──────────────────────────────────────────────────────────
      .addCase(getDashboardStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDashboardStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dashboardStats = action.payload;
      })
      .addCase(getDashboardStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // ── User Management ────────────────────────────────────────────────────
      .addCase(getUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = action.payload.data;
        state.totalPages = action.payload.last_page;
        state.currentPage = action.payload.current_page;
        state.totalItems = action.payload.total;
      })
      .addCase(getUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(toggleUserStatus.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(toggleUserStatus.fulfilled, (state, action) => {
        state.actionLoading = false;
        const { userId, is_active } = action.payload;
        const user = state.users.find((u) => u.id === userId);
        if (user) user.is_active = is_active;
        state.success = "User status updated successfully";
      })
      .addCase(toggleUserStatus.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      .addCase(updateUserRole.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(updateUserRole.fulfilled, (state, action) => {
        state.actionLoading = false;
        const { userId, role } = action.payload;
        const user = state.users.find((u) => u.id === userId);
        if (user) user.role = role;
        state.success = "User role updated successfully";
      })
      .addCase(updateUserRole.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      // ── KYC Management ─────────────────────────────────────────────────────
      .addCase(getPendingKyc.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPendingKyc.fulfilled, (state, action) => {
        state.isLoading = false;
        state.pendingKyc = action.payload;
      })
      .addCase(getPendingKyc.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(getAllKycDocuments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAllKycDocuments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allKycDocuments = action.payload.data;
        state.totalPages = action.payload.last_page;
        state.currentPage = action.payload.current_page;
        state.totalItems = action.payload.total;
      })
      .addCase(getAllKycDocuments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(approveKyc.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(approveKyc.fulfilled, (state, action) => {
        state.actionLoading = false;
        const { kycId } = action.payload;
        const pendingIndex = state.pendingKyc.findIndex((k) => k.id === kycId);
        if (pendingIndex !== -1) state.pendingKyc.splice(pendingIndex, 1);
        const kycDoc = state.allKycDocuments.find((k) => k.id === kycId);
        if (kycDoc) kycDoc.status = "approved";
        state.success = "KYC approved successfully";
      })
      .addCase(approveKyc.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      .addCase(rejectKyc.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(rejectKyc.fulfilled, (state, action) => {
        state.actionLoading = false;
        const { kycId } = action.payload;
        const pendingIndex = state.pendingKyc.findIndex((k) => k.id === kycId);
        if (pendingIndex !== -1) state.pendingKyc.splice(pendingIndex, 1);
        const kycDoc = state.allKycDocuments.find((k) => k.id === kycId);
        if (kycDoc) kycDoc.status = "rejected";
        state.success = "KYC rejected successfully";
      })
      .addCase(rejectKyc.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      // ── Deposit Management ─────────────────────────────────────────────────
      .addCase(getPendingDeposits.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPendingDeposits.fulfilled, (state, action) => {
        state.isLoading = false;
        state.pendingDeposits = action.payload;
      })
      .addCase(getPendingDeposits.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(getAllDeposits.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAllDeposits.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allDeposits = action.payload.data;
        state.totalPages = action.payload.last_page;
        state.currentPage = action.payload.current_page;
        state.totalItems = action.payload.total;
      })
      .addCase(getAllDeposits.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(confirmDeposit.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(confirmDeposit.fulfilled, (state, action) => {
        state.actionLoading = false;
        const { transactionId } = action.payload;
        const pendingIndex = state.pendingDeposits.findIndex(
          (t) => t.id === transactionId,
        );
        if (pendingIndex !== -1) state.pendingDeposits.splice(pendingIndex, 1);
        const deposit = state.allDeposits.find((t) => t.id === transactionId);
        if (deposit) deposit.status = "completed";
        state.success = "Deposit confirmed successfully";
      })
      .addCase(confirmDeposit.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      .addCase(rejectDeposit.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(rejectDeposit.fulfilled, (state, action) => {
        state.actionLoading = false;
        const { transactionId } = action.payload;
        const pendingIndex = state.pendingDeposits.findIndex(
          (t) => t.id === transactionId,
        );
        if (pendingIndex !== -1) state.pendingDeposits.splice(pendingIndex, 1);
        const deposit = state.allDeposits.find((t) => t.id === transactionId);
        if (deposit) deposit.status = "rejected";
        state.success = "Deposit rejected successfully";
      })
      .addCase(rejectDeposit.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      // ── Withdrawal Management ──────────────────────────────────────────────
      .addCase(getPendingWithdrawals.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPendingWithdrawals.fulfilled, (state, action) => {
        state.isLoading = false;
        state.pendingWithdrawals = action.payload;
      })
      .addCase(getPendingWithdrawals.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(getAllWithdrawals.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAllWithdrawals.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allWithdrawals = action.payload.data;
        state.totalPages = action.payload.last_page;
        state.currentPage = action.payload.current_page;
        state.totalItems = action.payload.total;
      })
      .addCase(getAllWithdrawals.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(processWithdrawal.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(processWithdrawal.fulfilled, (state, action) => {
        state.actionLoading = false;
        const { transactionId } = action.payload;
        const pendingIndex = state.pendingWithdrawals.findIndex(
          (t) => t.id === transactionId,
        );
        if (pendingIndex !== -1)
          state.pendingWithdrawals.splice(pendingIndex, 1);
        const withdrawal = state.allWithdrawals.find(
          (t) => t.id === transactionId,
        );
        if (withdrawal) withdrawal.status = "completed";
        state.success = "Withdrawal processed successfully";
      })
      .addCase(processWithdrawal.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      .addCase(rejectWithdrawal.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(rejectWithdrawal.fulfilled, (state, action) => {
        state.actionLoading = false;
        const { transactionId } = action.payload;
        const pendingIndex = state.pendingWithdrawals.findIndex(
          (t) => t.id === transactionId,
        );
        if (pendingIndex !== -1)
          state.pendingWithdrawals.splice(pendingIndex, 1);
        const withdrawal = state.allWithdrawals.find(
          (t) => t.id === transactionId,
        );
        if (withdrawal) withdrawal.status = "rejected";
        state.success = "Withdrawal rejected successfully";
      })
      .addCase(rejectWithdrawal.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      // ── XRP Address Management ─────────────────────────────────────────────
      .addCase(getXrpAddresses.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getXrpAddresses.fulfilled, (state, action) => {
        state.isLoading = false;
        state.xrpAddresses = action.payload;
      })
      .addCase(getXrpAddresses.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(createXrpAddress.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(createXrpAddress.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.xrpAddresses.unshift(action.payload.address);
        state.success = action.payload.message;
      })
      .addCase(createXrpAddress.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      .addCase(updateXrpAddress.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(updateXrpAddress.fulfilled, (state, action) => {
        state.actionLoading = false;
        const updatedAddress = action.payload.address;
        const index = state.xrpAddresses.findIndex(
          (a) => a.id === updatedAddress.id,
        );
        if (index !== -1) state.xrpAddresses[index] = updatedAddress;
        state.success = action.payload.message;
      })
      .addCase(updateXrpAddress.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      .addCase(deleteXrpAddress.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(deleteXrpAddress.fulfilled, (state, action) => {
        state.actionLoading = false;
        const { id } = action.payload;
        state.xrpAddresses = state.xrpAddresses.filter((a) => a.id !== id);
        state.success = action.payload.message;
      })
      .addCase(deleteXrpAddress.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      .addCase(uploadQrCode.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(uploadQrCode.fulfilled, (state, action) => {
        state.actionLoading = false;
        const { id, qr_code_url } = action.payload;
        const address = state.xrpAddresses.find((a) => a.id === id);
        if (address) address.qr_code_url = qr_code_url;
        state.success = action.payload.message;
      })
      .addCase(uploadQrCode.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      // ── Airdrop Management ─────────────────────────────────────────────────
      .addCase(getAirdrops.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAirdrops.fulfilled, (state, action) => {
        state.isLoading = false;
        state.airdrops = action.payload;
      })
      .addCase(getAirdrops.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(createAirdrop.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(createAirdrop.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.airdrops.unshift(action.payload.airdrop);
        state.success = action.payload.message;
      })
      .addCase(createAirdrop.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      .addCase(toggleAirdropStatus.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        const airdrop = state.airdrops.find((a) => a.id === id);
        if (airdrop) airdrop.status = status;
        state.success = "Airdrop status updated successfully";
      })

      .addCase(distributeAirdrop.fulfilled, (state, action) => {
        state.success = action.payload.message;
      })

      // ── Trading Management ─────────────────────────────────────────────────
      .addCase(getTradingSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getTradingSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tradingSettings = action.payload;
      })
      .addCase(getTradingSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(updateTradingSettings.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(updateTradingSettings.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.tradingSettings = action.payload.settings;
        state.success = action.payload.message;
      })
      .addCase(updateTradingSettings.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      .addCase(toggleTradingStatus.fulfilled, (state, action) => {
        if (state.tradingSettings) {
          state.tradingSettings.is_active = action.payload.is_active;
        }
        state.success = action.payload.message;
      })

      .addCase(manualDistribution.fulfilled, (state, action) => {
        if (state.tradingSettings) {
          state.tradingSettings.last_distribution =
            action.payload.last_distribution;
          state.tradingSettings.total_distributed =
            action.payload.total_distributed;
          state.tradingSettings.total_users_earning =
            action.payload.total_users_earning;
        }
        state.success = action.payload.message;
      })

      .addCase(getTradingStatistics.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getTradingStatistics.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tradingStatistics = action.payload;
      })
      .addCase(getTradingStatistics.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // ── System Actions ─────────────────────────────────────────────────────
      .addCase(recalculateEarnings.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(recalculateEarnings.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.success = action.payload.message;
      })
      .addCase(recalculateEarnings.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      .addCase(checkSystemHealth.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkSystemHealth.fulfilled, (state) => {
        state.isLoading = false;
        state.success = "System health check completed";
      })
      .addCase(checkSystemHealth.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearSuccess, resetAdminState, setCurrentPage } =
  adminSlice.actions;
export default adminSlice.reducer;
